import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

const CLOUDFLARE_PROFILES_URL = 'https://pub-2a10bd66114a4441b95e9249adee01cf.r2.dev';

const ProfileImage = React.memo(({ url, alt, className }) => {
  const [error, setError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const previousUrl = useRef(url);

  // Función para limpiar y obtener la URL completa
  const getFullUrl = useCallback((imageUrl) => {
    if (!imageUrl) return null;
    
    // Si ya es una URL completa, retornarla
    if (imageUrl.startsWith('http')) return imageUrl;
    
    // Limpiar la URL de /profiles/ u otros prefijos
    const cleanUrl = imageUrl
      .replace('/profiles/', '')
      .replace('profiles/', '')
      .replace(/^\//, '');
    
    // Construir la URL completa
    const fullUrl = `${CLOUDFLARE_PROFILES_URL}/${cleanUrl}`;
    
    return fullUrl;
  }, []);

  // URL completa memoizada
  const fullUrl = useMemo(() => getFullUrl(url), [url, getFullUrl]);

  // Extraer iniciales para avatar de respaldo
  const initials = useMemo(() => {
    if (!alt) return 'IN';
    
    return alt
      .split(' ')
      .map(word => word[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  }, [alt]);

  // Efecto para manejar cambios de URL
  useEffect(() => {
    if (previousUrl.current !== url) {
      setIsLoaded(false);
      setError(false);
      previousUrl.current = url;
      
      console.log('URL de imagen de perfil cambiada:', {
        previousUrl: previousUrl.current,
        newUrl: url,
        processedUrl: getFullUrl(url)
      });
    }
  }, [url, getFullUrl]);

  const handleError = useCallback((e) => {
    if (!error) {
      console.error('Error cargando imagen de perfil:', {
        originalUrl: url,
        processedUrl: fullUrl,
        error: e.message
      });
      setError(true);
    }
  }, [error, url, fullUrl]);

  const handleLoad = useCallback(() => {
    if (!isLoaded) {
      setIsLoaded(true);
    }
  }, [isLoaded, url, fullUrl]);

  // Si no hay URL o hay error, mostrar avatar con iniciales
  if (!fullUrl || error) {
    return (
      <div 
        className={`flex items-center justify-center bg-blue-100 text-blue-700 font-bold ${className}`}
        aria-label={alt}
      >
        {initials}
      </div>
    );
  }

  // Si hay URL, intentar cargar la imagen
  return (
    <img
      src={fullUrl}
      alt={alt}
      className={className}
      onError={handleError}
      onLoad={handleLoad}
    />
  );
});

ProfileImage.displayName = 'ProfileImage';

export default ProfileImage;